import React, { FC, useState, ChangeEvent, useEffect } from 'react'
// import { H3 } from 'components/base/typography/headings'
import IconArrowLeft from 'icons/arrow-left.svg'
import { StrippedButton } from 'components/base/buttons/buttons'
import StorefrontCategoryTree, { StorefrontCategoryTreeItem } from '@local-types/categoryTrees/storefrontCategoryTree'
import AnimatedContainer from 'components/base/animatedContainer/animatedContainer'
import { ActiveFilters } from 'reducers/filtersReducer'
import { useDebounce } from 'use-debounce'
import StorefrontFilters from '@local-types/filters/storefrontFilters'
import { allergenFilterList, AllergenFilter } from '../allergenFilterList'
import { HR, HRHideHandheld } from 'components/base/typography/misc'
import { FilterButton, FilterButtonHideHandheld } from 'components/base/buttons/filterButton'
import { Input } from 'components/base/forms/input'
import useFilters from 'hooks/useFilters'
import * as StyledList from '../filteredProductsList.styles'

interface Props {
	mainTreeFilter: {
		item: StorefrontCategoryTree | null | undefined
		isRoot: boolean
	}
	activeFilters: ActiveFilters
	filters: StorefrontFilters | null
	handleMainTreePrevClick: () => void
	handleMainTreeItemClick: (treeItem: StorefrontCategoryTreeItem, activeSiblings?: StorefrontCategoryTreeItem[]) => void
	handleFilterTreeItemClick: (treeItem: StorefrontCategoryTreeItem) => void
	handleAllergenFilterClick: (allergenFilter: AllergenFilter) => void
	clearAllergenFilters: () => void
}

const ProductFilters: FC<Props> = ({
	mainTreeFilter,
	activeFilters,
	filters,
	handleMainTreePrevClick,
	handleMainTreeItemClick,
	handleFilterTreeItemClick,
	handleAllergenFilterClick,
	clearAllergenFilters,
}) => {
	const [searchValue, setSearchValue] = useState(activeFilters.search)
	const { setFilterSearch } = useFilters()
	const [debouncedValue] = useDebounce(searchValue, 300)
	const activeChildren = mainTreeFilter.item?.children?.filter(c => activeFilters.catTreePath.includes(c.categoryId))
	const mainAllergens = allergenFilterList.filter(x => x.main)
	const [allergensOpen, setAllergensOpen] = useState(false)
	const [clearSearch, setClearSearch] = useState(false)

	useEffect(() => {
		if (clearSearch) {
			setSearchValue('')
			setClearSearch(false)
		}
		if (activeFilters.search !== searchValue) setSearchValue(activeFilters.search)
	}, [activeFilters])

	const handleSearchValue = (e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault()
		setSearchValue(e.target.value)
	}

	useEffect(() => {
		setFilterSearch(debouncedValue)
	}, [debouncedValue])

	const onMoreAllergensClick = () => {
		setAllergensOpen(!allergensOpen)
	}

	const handleClearFilters = () => {
		clearAllergenFilters()
		setClearSearch(true)
	}

	return (
		<>
			{mainTreeFilter.item && (
				<StyledList.MainTreeFilterWrapper>
					<StyledList.FilterHeader>
						<StyledList.BackButton active={mainTreeFilter.isRoot}>
							<StrippedButton type="button" onClick={handleMainTreePrevClick}>
								<IconArrowLeft />
								<span>Til baka</span>
							</StrippedButton>
						</StyledList.BackButton>
					</StyledList.FilterHeader>
					<HRHideHandheld mt="1.5rem" mb="1.5rem" />
					{mainTreeFilter.item.children?.map((child, index) => {
						return (
							<FilterButtonHideHandheld
								onClick={handleMainTreeItemClick.bind(
									null,
									child,
									child.children.length <= 0 ? activeChildren : undefined,
									mainTreeFilter.item ? mainTreeFilter.item.children : undefined
								)}
								key={child.id}
								mb={index === (mainTreeFilter.item?.children?.length || 0) - 1 ? '' : '1.5rem'}
								active={
									activeFilters.catTreePath.includes(child.categoryId) || activeFilters.catTreePath.includes(child.slug)
								}
								allergensFilter={false}
								isLastChild={child.children.length <= 0}>
								{child.name}
							</FilterButtonHideHandheld>
						)
					})}
					{mainTreeFilter.item.children?.length !== 0 && <HR mt="1.5rem" mb="1.5rem" />}
				</StyledList.MainTreeFilterWrapper>
			)}
			<AnimatedContainer heading="Vöruleit">
				<StyledList.SearchLabel id="filter-search-label" htmlFor="product-filter-search">
					Leit
				</StyledList.SearchLabel>
				<Input
					type="text"
					id="product-filter-search"
					name="product-filter-search"
					aria-label="Leita að vöru"
					aria-labelledby="filter-search-label"
					placeholder="Leita að vöru"
					value={searchValue}
					onChange={handleSearchValue}
					autoComplete="off"
				/>
			</AnimatedContainer>
			{filters?.categoryFilterTrees?.map(
				tree =>
					tree.children?.length > 0 && (
						<AnimatedContainer key={tree.id} heading={tree.name}>
							{tree.children?.map((item, index) => (
								<FilterButton
									onClick={handleFilterTreeItemClick.bind(null, item)}
									key={item.id}
									mb={index === (tree.children?.length || 0) - 1 ? '' : '1.5rem'}
									green
									active={
										activeFilters.catTreeFilterIds.includes(item.categoryId) ||
										activeFilters.catTreeFilterIds.includes(item.slug)
									}
									allergensFilter={false}>
									{item.name}
								</FilterButton>
							))}
						</AnimatedContainer>
					)
			)}
			<AnimatedContainer heading="Ofnæmi og óþol">
				{!allergensOpen
					? mainAllergens.map(allergenFilter => (
							<FilterButton
								onClick={handleAllergenFilterClick.bind(null, allergenFilter)}
								key={allergenFilter.id}
								mb="1.5rem"
								active={activeFilters.allergenFilterIds.includes(allergenFilter.id)}
								allergensFilter>
								{allergenFilter.label}
							</FilterButton>
					  ))
					: allergenFilterList.map(allergenFilter => (
							<FilterButton
								onClick={handleAllergenFilterClick.bind(null, allergenFilter)}
								key={allergenFilter.id}
								mb="1.5rem"
								active={activeFilters.allergenFilterIds.includes(allergenFilter.id)}
								allergensFilter>
								{allergenFilter.label}
							</FilterButton>
					  ))}
				<StyledList.FilterSubHeading onClick={onMoreAllergensClick}>{`Sjá ${
					allergensOpen ? 'færri' : 'alla'
				} ofnæmisvalda`}</StyledList.FilterSubHeading>
			</AnimatedContainer>
			<StyledList.ClearButton hidden={activeFilters.allergenFilterIds.length <= 0 && searchValue.length <= 0}>
				<StrippedButton type="button" secondary onClick={handleClearFilters}>
					Hreinsa síur
				</StrippedButton>
			</StyledList.ClearButton>
		</>
	)
}

export default ProductFilters
